import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { isApiError } from '../../api/errors';
import { useUpdateUser } from './userMutations';

const EDITABLE_FIELDS = [
  'displayName',
  'email',
  'role',
  'accountType',
  'companyName',
  'vatId',
  'billingCountry',
  'billingCity',
  'billingZip',
  'billingStreet',
];

/**
 * Diff-Patch Edit-Form. Schickt nur tatsächlich geänderte Felder.
 * Spec: orbit-specs/done/user-crud-admin.md → "Bearbeiten"
 */
export function UserEditModal({ isOpen, user, onClose }) {
  // Form-State wird beim Mount aus dem User-Snapshot initialisiert.
  // Vertrag: Caller setzt `key={user?.id}` damit der Form-State bei
  // Wechsel des Users via Remount zurückgesetzt wird — kein setState-
  // in-useEffect-Pattern.
  const [form, setForm] = useState(() => snapshot(user));
  const [error, setError] = useState(null);
  const update = useUpdateUser();

  if (!user) return null;

  const isBusiness = form.accountType === 'business';
  const set = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const diff = computeDiff(snapshot(user), form);
  const hasChanges = Object.keys(diff).length > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!hasChanges) {
      setError('Keine Änderungen.');
      return;
    }
    try {
      await update.mutateAsync({ id: user.id, patch: diff });
      onClose();
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Speichern fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={update.isPending ? undefined : onClose} size="lg" backdrop="static">
      <ModalHeader toggle={update.isPending ? undefined : onClose}>
        Benutzer bearbeiten
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>E-Mail</Label>
                <Input type="email" value={form.email} onChange={set('email')} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Anzeigename</Label>
                <Input value={form.displayName ?? ''} onChange={set('displayName')} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Rolle</Label>
                <Input type="select" value={form.role} onChange={set('role')}>
                  <option value="customer">customer</option>
                  <option value="staff">staff</option>
                  <option value="admin">admin</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Konto-Typ</Label>
                <Input type="select" value={form.accountType} onChange={set('accountType')}>
                  <option value="private">private</option>
                  <option value="business">business</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {isBusiness && (
            <>
              <hr />
              <h6 className="text-muted small text-uppercase">B2B-Block</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Firma</Label>
                    <Input value={form.companyName ?? ''} onChange={set('companyName')} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>USt-IdNr</Label>
                    <Input value={form.vatId ?? ''} onChange={set('vatId')} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label>Land</Label>
                    <Input value={form.billingCountry ?? ''} onChange={set('billingCountry')} maxLength={2} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>PLZ</Label>
                    <Input value={form.billingZip ?? ''} onChange={set('billingZip')} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Stadt</Label>
                    <Input value={form.billingCity ?? ''} onChange={set('billingCity')} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Straße</Label>
                <Input value={form.billingStreet ?? ''} onChange={set('billingStreet')} />
              </FormGroup>
            </>
          )}

          {hasChanges && (
            <Alert color="info" className="small mb-0">
              Diff: <code>{JSON.stringify(diff)}</code>
            </Alert>
          )}
          {error && <Alert color="danger" className="mt-2 mb-0">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={update.isPending}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={!hasChanges || update.isPending}>
            {update.isPending ? 'Speichern…' : 'Speichern'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function snapshot(user) {
  if (!user) return {};
  const s = {};
  EDITABLE_FIELDS.forEach((k) => {
    s[k] = user[k] ?? '';
  });
  return s;
}

function computeDiff(orig, next) {
  const diff = {};
  EDITABLE_FIELDS.forEach((k) => {
    const a = (orig[k] ?? '') === '' ? null : orig[k];
    const b = (next[k] ?? '') === '' ? null : next[k];
    if (a !== b) diff[k] = b;
  });
  return diff;
}
